import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionCodes, PermissionGroup } from "@/types/permissions.types";

type PermissionGroupDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (group: Omit<PermissionGroup, "id">) => void;
    editGroup?: PermissionGroup;
    permissionCodes?: PermissionCodes;
    isLoading?: boolean;
};

const PermissionGroupDialog: React.FC<PermissionGroupDialogProps> = ({
    open,
    onClose,
    onSave,
    editGroup,
    permissionCodes,
    isLoading = false,
}) => {
    const [groupName, setGroupName] = useState("");
    const [groupColor, setGroupColor] = useState("#4A90E2");
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (editGroup) {
            setGroupName(editGroup.name);
            setGroupColor(editGroup.color);
            setSelectedPermissions(editGroup.permissions);
        } else {
            setGroupName("");
            setGroupColor("#4A90E2");
            setSelectedPermissions([]);
        }

        // When the dialog opens, focus the input field
        if (open) {
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 100);
        }
    }, [editGroup, open]);

    const handleSave = () => {
        const group: Omit<PermissionGroup, "id"> = {
            name: groupName,
            permissions: selectedPermissions,
            color: groupColor,
        };
        onSave(group);
        onClose();
    };

    const handlePermissionChange = (permission: string, checked: boolean) => {
        setSelectedPermissions((prev) => {
            if (checked) {
                return [...prev, permission];
            } else {
                return prev.filter((p) => p !== permission);
            }
        });
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogContent
                    sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}
                >
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }

    // Get service names containing permission codes
    const serviceNames = permissionCodes ? Object.keys(permissionCodes) : [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{editGroup ? t("edit_permission_group") : t("add_permission_group")}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
                        <TextField
                            fullWidth
                            label={t("group_name")}
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            inputRef={inputRef}
                            autoFocus
                            onFocus={(e) => e.target.select()}
                            inputProps={{
                                spellCheck: false,
                            }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 200 }}>
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 1,
                                    backgroundColor: groupColor,
                                    border: "2px solid #e0e0e0",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        transform: "scale(1.05)",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    },
                                }}
                                onClick={() => document.getElementById("color-picker")?.click()}
                            />
                            <input
                                id="color-picker"
                                type="color"
                                value={groupColor}
                                onChange={(e) => setGroupColor(e.target.value)}
                                style={{
                                    width: 0,
                                    height: 0,
                                    padding: 0,
                                    border: "none",
                                    visibility: "hidden",
                                }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary", pt: 1 }}>
                                {groupColor.toUpperCase()}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        {t("permissions")}
                    </Typography>

                    {serviceNames.length === 0 ? (
                        <Alert severity="info">{t("no_permission_codes_have_been_defined_yet")}</Alert>
                    ) : (
                        serviceNames
                            .sort((a, b) => a.localeCompare(b))
                            .map((serviceName) => {
                                const serviceData = permissionCodes?.[serviceName];
                                const hasMessage = serviceData && "message" in serviceData;

                                return (
                                    <Accordion key={serviceName} sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography>{serviceName}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {hasMessage ? (
                                                <Alert severity="info" sx={{ width: "100%" }}>
                                                    {t("no_available_permission_codes_for_this_service")}
                                                </Alert>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 0.25,
                                                        maxHeight: "220px",
                                                        overflowY: "auto",
                                                    }}
                                                >
                                                    {serviceData &&
                                                        Object.keys(serviceData)
                                                            .sort((a, b) => a.localeCompare(b))
                                                            .map((permissionCode) => (
                                                                <FormControlLabel
                                                                    key={permissionCode}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={selectedPermissions.includes(
                                                                                serviceData[permissionCode]
                                                                            )}
                                                                            onChange={(e) =>
                                                                                handlePermissionChange(
                                                                                    serviceData[permissionCode],
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: "0.8rem",
                                                                                wordBreak: "break-word",
                                                                                lineHeight: 1.2,
                                                                            }}
                                                                        >
                                                                            {permissionCode}
                                                                        </Typography>
                                                                    }
                                                                    sx={{ margin: 0 }}
                                                                />
                                                            ))}
                                                </Box>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("cancel")}</Button>
                <Button onClick={handleSave} variant="contained" disabled={!groupName.trim()}>
                    {t("save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PermissionGroupDialog;
